import React, { createContext, useState, useEffect, useContext } from "react";
import { auth, db } from "../config/firebase";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = async (email, password, rememberMe = false) => {
    try {
      const persistence = rememberMe
        ? browserLocalPersistence
        : browserSessionPersistence;
      await setPersistence(auth, persistence);

      const result = await signInWithEmailAndPassword(auth, email, password);

      // Récupérer / créer profil utilisateur en Firestore (silencieusement)
      try {
        const userRef = doc(db, "USERS", result.user.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          setUserRole(userData?.role ?? null);
          setUserProfile(userData);

          // Mettre à jour lastLogin avec serverTimestamp si autorisé
          try {
            await updateDoc(userRef, { lastLogin: serverTimestamp() });
          } catch (e) {
            // permission denied possible -> ignorer proprement
            console.debug("updateDoc lastLogin skipped:", e?.code || e);
          }
        } else {
          // Créer profil de base si absent (tentative silencieuse)
          const basicProfile = {
            uid: result.user.uid,
            email: result.user.email,
            role: "commercial",
            createdAt: serverTimestamp(),
            lastLogin: serverTimestamp(),
          };
          try {
            await setDoc(userRef, basicProfile);
          } catch (e) {
            console.debug("setDoc basicProfile skipped:", e?.code || e);
          }
          setUserRole(basicProfile.role);
          setUserProfile(basicProfile);
        }
      } catch (firestoreError) {
        console.warn(
          "Erreur Firestore lors du login (profil non chargé) :",
          firestoreError?.code ?? firestoreError
        );
        // fallback minimal profile
        setUserRole("commercial");
        setUserProfile({
          uid: result.user.uid,
          email: result.user.email,
          role: "commercial",
        });
      }

      return result;
    } catch (error) {
      console.error("Erreur de connexion:", error);
      throw error;
    }
  };

  const logout = () => signOut(auth);

  useEffect(() => {
    const readProfileWithFallbacks = async (uid, forceTokenRefresh = false) => {
      // Try reading primary collection "USERS", then "users"
      const collections = ["USERS", "users"];
      if (forceTokenRefresh && auth.currentUser) {
        try {
          await auth.currentUser.getIdTokenResult(true);
        } catch (tErr) {
          console.debug("Token refresh failed:", tErr);
        }
      }

      for (const col of collections) {
        try {
          const userRef = doc(db, col, uid);
          const snap = await getDoc(userRef);
          if (snap.exists()) return snap.data();
        } catch (e) {
          // If permission-denied, continue to next fallback after attempting token refresh once
          if (e?.code === "permission-denied") {
            console.warn(`Permission denied reading ${col} for ${uid}`);
            // try refreshing token once then continue loop to retry same collection
            if (!forceTokenRefresh) {
              return await readProfileWithFallbacks(uid, true);
            }
          } else {
            console.debug(`Read ${col} failed:`, e?.code || e);
          }
        }
      }

      return null;
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (!user) {
        setUserRole(null);
        setUserProfile(null);
        setLoading(false);
        return;
      }

      try {
        // Debug: log claims (non-fatal)
        try {
          const tokenResult = await user.getIdTokenResult(false);
          console.debug("ID token claims:", tokenResult.claims);
        } catch (tErr) {
          console.debug("Impossible d'obtenir les claims:", tErr);
        }

        const profile = await readProfileWithFallbacks(user.uid);
        if (profile) {
          setUserRole(profile?.role ?? null);
          setUserProfile(profile);
        } else {
          // profil absent ou non lisible -> fallback minimal
          setUserRole(null);
          setUserProfile({
            uid: user.uid,
            email: user.email,
          });
        }
      } catch (err) {
        console.error("Erreur lors du chargement du profil:", err);
        if (err?.code === "permission-denied") {
          console.warn(
            "Permission Firestore refusée pour la lecture du profil. Vérifier vos rules Firestore."
          );
        }
        setUserRole(null);
        setUserProfile({
          uid: user.uid,
          email: user.email,
        });
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    userProfile,
    loading,
    login,
    logout,
    isAdmin: userRole === "admin",
    isCommercial: userRole === "commercial",
  };

  // fallback pour éviter écran blanc si l'initialisation reste bloquée
  React.useEffect(() => {
    const t = setTimeout(() => {
      setLoading(false);
      console.warn("AuthContext: loading fallback déclenché (10s)");
    }, 10000);
    return () => clearTimeout(t);
  }, []);

  // Rendre toujours les enfants (évite écran blanc). Les composants peuvent utiliser `loading` si besoin.
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
